// Drift: ordrer, betalinger, Betalingscenteret og kundens aflysning på Supabase (del af SupabaseApi – se supabase.ts).
// Reglerne og beregningerne ligger i 20261029200200_betalinger_drift.sql (samme regler som testversionen): ordren regnes ud
// fra bookinger og betalinger, manuelle registreringer kræver et bevis, og faktura/kvittering/betalingslink lægges i beskeder.
// Kunderne betaler via de personlige links i betalingsmailen (edge-funktionerne mobilepay og viva-webhook). Indbetalinger fra
// Viva og MobilePay hentes, afstemmes og bogføres i e-conomic af edge-funktionen indbetalinger (20261029899000_afstemning.sql):
// databasen viser dem og registrerer afstemningen (kun én gang); Freemis vurdering er serverens (src/core/afstemning.ts).
import { ApiFejl, type Api, type Betalingscenter, type Betalingsopsaetning, type BetalingVisning, type EconomicLister, type IndbetalingVisning, type OrdreDetalje, type OrdreVisning } from './api';
import { gemteNoegler, tjekBetalingsnoegler } from '../../core/betalingsnoegler';
import { email as tjekEmail, rens } from '../../core/validering';
import { tilVisning } from './sbKontor';
import type { SbKontekst } from './sbKatalog';

type R = Record<string, unknown>;
type Raa = Omit<OrdreVisning, 'bookinger'> & { bookinger: Parameters<typeof tilVisning>[0][] };
const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);
const tilOrdre = (o: Raa): OrdreVisning => ({ ...o, bookinger: o.bookinger.map(tilVisning) });
const bevis = (b: string | undefined) => {
  if (rens(b ?? '', 300).length < 8) throw fejl('Skriv hvordan betalingen er bekræftet (fx "Set på kontoudtog 12/10" eller "Kontant til steameren, kvittering nr. 14"). Brug Betalingscenter til MobilePay, Viva og bank.', { bevis: 'Skriv hvordan betalingen er bekræftet' });
};
const mail = (e: string | undefined) => { if (e && !tjekEmail(e)) throw fejl('Skriv en gyldig e-mail.', { email: 'Ugyldig e-mail.' }); };

export class SbBetalinger {
  constructor(private readonly c: SbKontekst) {}
  private async rpc<T>(navn: string, args: R = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  private async skriv(navn: string, args: R) { await this.rpc(navn, args); this.c.skift(); }

  // ---------- Ordrer ----------
  ordrer: Api['ordrer'] = async (f) => ((await this.rpc<Raa[] | null>('ordrer_liste', { soeg: rens(f.soeg ?? '', 80), p_betaling: f.betaling ?? null })) ?? []).map(tilOrdre);
  betalingSide: Api['betalingSide'] = async (f) => {
    const x = await this.rpc<Omit<Awaited<ReturnType<Api['betalingSide']>>, 'raekker'> & { raekker: Raa[] | null }>('betalinger_side', { p: { ...f, soeg: rens(f.soeg ?? '', 80) } });
    return { ...x, raekker: (x.raekker ?? []).map(tilOrdre) };
  };
  ordre: Api['ordre'] = async (id) => {
    const o = await this.rpc<(Raa & Pick<OrdreDetalje, 'log' | 'betalinger'>) | null>('ordre_detalje', { p_id: id });
    return o ? { ...tilOrdre(o), log: o.log ?? [], betalinger: o.betalinger ?? [] } : null;
  };
  saetOrdreBetaling: Api['saetOrdreBetaling'] = async (id, status, d = {}) => {
    if (status === 'betalt' || status === 'delvis') bevis(d.bevis);
    if (status === 'delvis' && !(Math.round(d.beloeb ?? 0) > 0)) throw fejl('Skriv et beløb, der er mindre end det, der mangler.', { beloeb: 'Ugyldigt beløb.' });
    await this.skriv('saet_ordre_betaling', { p_id: id, p_status: status, p_beloeb: d.beloeb == null ? null : Math.round(d.beloeb), p_metode: d.metode ?? null, p_bevis: d.bevis ?? null });
  };
  saetVilkaar: Api['saetVilkaar'] = (id, accepteret) => this.skriv('saet_vilkaar', { p_id: id, p_accepteret: accepteret });
  sendFaktura: Api['sendFaktura'] = async (id, email, gebyr = true) => {
    mail(email);
    const nr = await this.rpc<string>('send_faktura', { p_id: id, p_email: email || null, p_gebyr: gebyr });
    this.c.skift();
    return { nr };
  };
  sendKvittering: Api['sendKvittering'] = async (id, til) => {
    mail(til.email);
    await this.skriv('send_kvittering', { p_id: id, p_email: til.email === undefined ? null : til.email, p_sms: !!til.sms });
  };
  sendOrdreBetalingslink: Api['sendOrdreBetalingslink'] = async (id, kanal, email) => {
    mail(email);
    await this.skriv('send_ordre_betalingslink', { p_id: id, p_kanal: kanal, p_email: email || null });
  };
  tilfoejRabatkode: Api['tilfoejRabatkode'] = async (booking_id, kode) => {
    if (!rens(kode, 30)) throw fejl('Skriv rabatkoden.');
    const r = await this.rpc<{ rabat: number; total: number }>('tilfoej_rabatkode', { p_booking: booking_id, p_kode: rens(kode, 30) });
    this.c.skift();
    return r;
  };

  // ---------- Betalinger ----------
  betalinger: Api['betalinger'] = async (f) => ((await this.rpc<BetalingVisning[] | null>('betalinger_liste', { p_status: f.status ?? null, soeg: rens(f.soeg ?? '', 80) })) ?? []);
  markerBetalt: Api['markerBetalt'] = async (id, metode, b) => { bevis(b); await this.skriv('marker_betalt', { p_id: Number(id), p_metode: metode, p_bevis: b }); };
  refunder: Api['refunder'] = (id) => this.skriv('refunder_betaling', { p_id: Number(id) });
  sendBetalingslink: Api['sendBetalingslink'] = (booking_id) => this.skriv('send_betalingslink', { p_booking: booking_id });
  fornyMedlemskaber: Api['fornyMedlemskaber'] = async (dato) => {
    const n = await this.rpc<number>('forny_medlemskaber', { p_dato: dato });
    this.c.skift();
    return n;
  };

  // ---------- Betalingscenteret og Freemis afstemning ----------
  /** Hvilke kilder er sat op for virksomheden (edge-funktionen indbetalinger) – aldrig nøglerne. */
  private async kildeStatus(): Promise<{ viva: boolean; mobilepay: boolean }> {
    const k = await this.c.kraevKlient();
    try {
      const { data, error } = await this.c.sb.functions.invoke(`indbetalinger?status=1&klient=${k}`, { method: 'GET' });
      const d = (error ? null : data) as { viva?: boolean; mobilepay?: boolean } | null;
      return { viva: !!d?.viva, mobilepay: !!d?.mobilepay };
    } catch { return { viva: false, mobilepay: false }; }
  }
  betalingscenter: Api['betalingscenter'] = async () => {
    const [d, st] = await Promise.all([
      this.rpc<Pick<Betalingscenter, 'aabne' | 'indbetalinger'> & { kilder: Record<string, { sidst: string | null; fejl: string | null }> }>('betalingscenter_data'),
      this.kildeStatus(),
    ]);
    const ind: IndbetalingVisning[] = d.indbetalinger ?? [];
    const dag = (iso: string) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Copenhagen' }).format(new Date(iso));
    const idag = dag(new Date().toISOString());
    const kilde = (id: 'mobilepay' | 'viva', navn: string) => {
      const x = d.kilder?.[id];
      return { id, navn, sidst: x?.sidst ?? null, status: !st[id] ? 'Ikke forbundet' : x?.fejl ? `Fejl: ${x.fejl}` : 'Forbundet' };
    };
    return {
      indbetalinger: ind, aabne: d.aabne,
      stat: {
        udestaaende: d.aabne.reduce((s, o) => s + o.mangler, 0), aabne: d.aabne.length,
        nye: ind.filter((x) => x.status === 'ny').length, sikre: ind.filter((x) => x.status === 'ny' && x.analyse?.sikker).length,
        afstemtIDag: ind.filter((x) => x.status === 'afstemt' && x.afstemt_tid && dag(x.afstemt_tid) === idag).length,
        betaltIDag: ind.filter((x) => x.status === 'afstemt' && dag(x.tid) === idag).reduce((s, x) => s + x.beloeb, 0),
      },
      kilder: [kilde('mobilepay', 'MobilePay'), kilde('viva', 'Viva Wallet'), { id: 'bank' as const, navn: 'Bank', status: 'Ikke tilgængelig via e-conomic', sidst: null }],
    };
  };
  /** Freemi (serveren) vurderer alle ventende betalinger og afstemmer de sikre. */
  pixieAfstem: Api['pixieAfstem'] = async () => {
    const r = await this.c.funktion<Awaited<ReturnType<Api['pixieAfstem']>>>('indbetalinger', { handling: 'afstem', klient: await this.c.kraevKlient() });
    this.c.skift();
    return r;
  };
  hentIndbetalinger: Api['hentIndbetalinger'] = async () => {
    const r = await this.c.funktion<{ nye: number; fejl: string[] }>('indbetalinger', { handling: 'hent', klient: await this.c.kraevKlient() });
    this.c.skift();
    if (!r.nye && r.fejl?.length) throw fejl(`Betalingerne kunne ikke hentes: ${r.fejl.join(' ')}`);
    return { nye: r.nye };
  };
  afstemIndbetaling: Api['afstemIndbetaling'] = (id, ordre_id, grund) => this.skriv('afstem_indbetaling', { p_id: Number(id), p_ordre: ordre_id, p_grund: rens(grund ?? '', 300) || null });
  ignorerIndbetaling: Api['ignorerIndbetaling'] = async (id, grund) => {
    if (rens(grund, 300).length < 3) throw fejl('Skriv hvorfor.', { grund: 'Skriv hvorfor' });
    await this.skriv('ignorer_indbetaling', { p_id: Number(id), p_grund: rens(grund, 300) });
  };
  fortrydAfstemning: Api['fortrydAfstemning'] = (id) => this.skriv('fortryd_afstemning', { p_id: Number(id) });

  // ---------- e-conomic ----------
  economic: Api['economic'] = () => this.rpc('economic_opsaetning');
  gemEconomic: Api['gemEconomic'] = async (d) => {
    const token = d.token.trim();
    if (token && !/^[A-Za-z0-9_.-]{10,200}$/.test(token)) throw fejl('Aftale-tokenet ser forkert ud. Kopiér det igen fra e-conomic.', { token: 'Ugyldigt token' });
    await this.skriv('gem_economic', { p: { token, aktiv: d.aktiv, kassekladde: d.kassekladde, konti: d.konti } });
  };
  fjernEconomic: Api['fjernEconomic'] = () => this.skriv('fjern_economic', {});
  economicLister: Api['economicLister'] = async () => {
    const r = await this.c.funktion<EconomicLister>('indbetalinger', { handling: 'lister', klient: await this.c.kraevKlient() });
    this.c.skift();
    return r;
  };
  economicBogfoerNu: Api['economicBogfoerNu'] = async () => {
    const r = await this.c.funktion<Awaited<ReturnType<Api['economicBogfoerNu']>>>('indbetalinger', { handling: 'bogfoer', klient: await this.c.kraevKlient() });
    this.c.skift();
    return r;
  };
  economicProevIgen: Api['economicProevIgen'] = (id) => this.skriv('economic_proev_igen', { p_id: id });

  /** Spørger edge-funktionen viva-webhook, om Viva er sat op for virksomheden (egne nøgler eller overgangsreserven) – aldrig nøglerne. */
  vivaStatus: Api['vivaStatus'] = async () => {
    const k = await this.c.kraevKlient();
    try {
      const { data, error } = await this.c.sb.functions.invoke(`viva-webhook?status=1&klient=${k}`, { method: 'GET' });
      if (error || !data) return { api: false, miljoe: null, egen: false };
      const d = data as { api?: boolean; miljoe?: string; egen?: boolean };
      return { api: !!d.api, miljoe: d.miljoe === 'live' || d.miljoe === 'demo' ? d.miljoe : null, egen: !!d.egen };
    } catch { return { api: false, miljoe: null, egen: false }; }
  };

  /** Spørger edge-funktionen mobilepay, om MobilePay-nøglerne er sat for virksomheden (aldrig selve nøglerne). */
  mobilepayStatus: Api['mobilepayStatus'] = async () => {
    const k = await this.c.kraevKlient();
    try {
      const { data, error } = await this.c.sb.functions.invoke(`mobilepay?status=1&klient=${k}`, { method: 'GET' });
      if (error || !data) return { api: false, miljoe: null, webhook: false, egen: false };
      const d = data as { api?: boolean; miljoe?: string; webhook?: boolean; egen?: boolean };
      return { api: !!d.api, miljoe: d.miljoe === 'live' || d.miljoe === 'test' ? d.miljoe : null, webhook: !!d.webhook, egen: !!d.egen };
    } catch { return { api: false, miljoe: null, webhook: false, egen: false }; }
  };

  // ---------- Virksomhedens egne betalingsnøgler (edge-funktionen betalingsnoegler + 20261030160000_betaling_pr_klient.sql) ----------
  betalingsopsaetning: Api['betalingsopsaetning'] = async () => {
    const o = await this.rpc<(Betalingsopsaetning & { viva: { webhookRef?: string } | null }) | null>('betalingsopsaetning');
    if (!o) return null;
    const base = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '');
    const { webhookRef, ...viva } = o.viva ?? {};
    return { ...o, viva: o.viva ? { ...(viva as NonNullable<Betalingsopsaetning['viva']>), webhookUrl: webhookRef ? `${base}/functions/v1/viva-webhook?k=${webhookRef}` : undefined } : null };
  };
  gemBetalingsnoegler: Api['gemBetalingsnoegler'] = async (udbyder, d) => {
    const gl = (await this.betalingsopsaetning())?.[udbyder];
    const t = tjekBetalingsnoegler(udbyder, d, gl ? gemteNoegler(gl.felter) : null);   // samme regel som databasen – fejlen ved feltet med det samme
    if (t.fejl !== null) throw fejl(t.fejl, t.felter);
    const r = await this.c.funktion<{ ok: boolean; besked: string }>('betalingsnoegler', { klient: await this.c.kraevKlient(), udbyder, handling: 'gem', data: d });
    this.c.skift();
    return { ok: r.ok, besked: r.besked };
  };
  testBetalingsnoegler: Api['testBetalingsnoegler'] = async (udbyder, d) => {
    const r = await this.c.funktion<{ ok: boolean; besked: string }>('betalingsnoegler', { klient: await this.c.kraevKlient(), udbyder, handling: 'test', ...(d ? { data: d } : {}) });
    if (!d) this.c.skift();   // de gemte nøgler: resultatet står nu på opsætningen
    return { ok: r.ok, besked: r.besked };
  };
  fjernBetalingsnoegler: Api['fjernBetalingsnoegler'] = async (udbyder) => {
    await this.c.funktion('betalingsnoegler', { klient: await this.c.kraevKlient(), udbyder, handling: 'fjern' });
    this.c.skift();
  };

  // ---------- Kunden ----------
  aflysPrisFor: Api['aflysPrisFor'] = (id) => this.rpc('aflys_pris', { p_booking: id });
  kundeAflys: Api['kundeAflys'] = async (id, accepterGebyr = false) => {
    try { await this.rpc('kunde_aflys', { p_booking: id, p_accepter: accepterGebyr }); }
    catch (e) { throw e instanceof ApiFejl && /Godkend gebyret/.test(e.message) ? fejl(e.message, { gebyr: 'kraeves' }) : e; }
    this.c.skift();
  };
  /** Online betaling (MobilePay/Viva) er ikke slået til endnu: kunden betaler, som virksomheden har aftalt (faktura/overførsel). */
  betal: Api['betal'] = async () => { throw fejl('Online betaling er ikke slået til endnu. Du betaler, som vi har aftalt med dig – kontakt os, hvis du er i tvivl.'); };
  /** I drift markerer edge-funktionen mobilepay betalingen, når MobilePay har trukket beløbet – siden viser kun svaret. */
  betalingRetur: Api['betalingRetur'] = async () => undefined;
  tilmeldKort: Api['tilmeldKort'] = async () => { throw fejl('Tilmelding af betalingskort er ikke slået til endnu. Vi kontakter dig om betalingen.'); };
  /** Efter bestillingen: login-linket er allerede sendt med bekræftelsen ("Din kundekonto er klar"), så her vises kun, hvor det er sendt. */
  kundeLoginEfterBestilling: Api['kundeLoginEfterBestilling'] = async (kode) => {
    const a = this.c.adresse();
    if (!a) throw fejl('Vi kunne ikke finde bestillingen.');
    const r = (await this.c.k(this.c.sb.rpc('bestilling_login', { vaert: a.vaert, sti: a.slug, p_kode: rens(kode, 12) }))) as { harKode: boolean; email: string; spaerret: boolean; fejl?: string };
    if (r.fejl) throw fejl(r.fejl);   // forkert kode: svaret (ikke en fejl), så databasen tæller forsøget
    if (!r.harKode && r.spaerret) throw fejl('Vi kan ikke sende et link lige nu. Log ind med en engangskode på mail i stedet.');
    return { harKode: r.harKode, email: r.email };
  };
}
